/** Range Planning "Items" detail page (Demo Mode only). Tabbed layout follows the same
 * pattern as RecipeV2Detail.tsx - see src/pages/RecipeV2/RecipeV2Detail.tsx. */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, CircularProgress, IconButton, Stack, SvgIcon, Tab, Tabs, Typography } from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { ArrowLeft } from '../../assets';
import useRangePlanningItemDetail from './hooks/useRangePlanningItemDetail';
import { ItemDetailTab, ItemLifecycleStatus } from './types';
import OverviewTab from './components/OverviewTab';
import SupplierDetailsTab from './components/SupplierDetailsTab';
import UomConversionsTab from './components/UomConversionsTab';
import PricingTab from './components/PricingTab';
import AllergensAttributesTab from './components/AllergensAttributesTab';
import NutritionTab from './components/NutritionTab';
import CarbonSustainabilityTab from './components/CarbonSustainabilityTab';
import CatalogueAvailabilityTab from './components/CatalogueAvailabilityTab';
import RecipeUsageTab from './components/RecipeUsageTab';
import InventoryMappingTab from './components/InventoryMappingTab';

const statusColor: Record<ItemLifecycleStatus, 'success' | 'warning' | 'default'> = {
  [ItemLifecycleStatus.Active]: 'success',
  [ItemLifecycleStatus.PendingReview]: 'warning',
  [ItemLifecycleStatus.Inactive]: 'default',
};

const ItemDetail = () => {
  const { t } = useTranslation('rangePlanningItems');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { item, isLoading, loadItem } = useRangePlanningItemDetail();
  const [tabValue, setTabValue] = useState<ItemDetailTab>(ItemDetailTab.Overview);

  useEffect(() => {
    if (id) loadItem(Number(id));
  }, [id, loadItem]);

  if (isLoading || !item) {
    return (
      <PageArea>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
          <CircularProgress />
        </Box>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Stack direction="row"
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}>
        <IconButton onClick={() => navigate('/range-planning/items')}
          aria-label={t('backToItems', 'Back to items')}>
          <SvgIcon><ArrowLeft /></SvgIcon>
        </IconButton>
        <PageTitle>{item.name}</PageTitle>
        <Chip label={t(`status.${item.status}`, item.status)}
          color={statusColor[item.status]}
          size="small" />
      </Stack>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mb: 4 }}>
        {item.itemNumber} - {item.category}
      </Typography>

      <Section>
        <Tabs value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tab label={t('tabs.overview', 'Overview')}
            value={ItemDetailTab.Overview} />
          <Tab label={t('tabs.supplierDetails', 'Supplier Details')}
            value={ItemDetailTab.SupplierDetails} />
          <Tab label={t('tabs.uomConversions', 'UOM & Conversions')}
            value={ItemDetailTab.UomConversions} />
          <Tab label={t('tabs.pricing', 'Pricing')}
            value={ItemDetailTab.Pricing} />
          <Tab label={t('tabs.allergensAttributes', 'Allergens & Attributes')}
            value={ItemDetailTab.AllergensAttributes} />
          <Tab label={t('tabs.nutrition', 'Nutrition')}
            value={ItemDetailTab.Nutrition} />
          <Tab label={t('tabs.carbonSustainability', 'Carbon & Sustainability')}
            value={ItemDetailTab.CarbonSustainability} />
          <Tab label={t('tabs.catalogueAvailability', 'Catalogue Availability')}
            value={ItemDetailTab.CatalogueAvailability} />
          <Tab label={t('tabs.recipeUsage', 'Recipe Usage')}
            value={ItemDetailTab.RecipeUsage} />
          <Tab label={t('tabs.inventoryMapping', 'Inventory Mapping')}
            value={ItemDetailTab.InventoryMapping} />
        </Tabs>

        {tabValue === ItemDetailTab.Overview && <OverviewTab item={item} />}
        {tabValue === ItemDetailTab.SupplierDetails && <SupplierDetailsTab item={item} />}
        {tabValue === ItemDetailTab.UomConversions && <UomConversionsTab item={item} />}
        {tabValue === ItemDetailTab.Pricing && <PricingTab item={item} />}
        {tabValue === ItemDetailTab.AllergensAttributes && <AllergensAttributesTab item={item} />}
        {tabValue === ItemDetailTab.Nutrition && <NutritionTab item={item} />}
        {tabValue === ItemDetailTab.CarbonSustainability && <CarbonSustainabilityTab item={item} />}
        {tabValue === ItemDetailTab.CatalogueAvailability && <CatalogueAvailabilityTab item={item} />}
        {tabValue === ItemDetailTab.RecipeUsage && <RecipeUsageTab item={item} />}
        {tabValue === ItemDetailTab.InventoryMapping && <InventoryMappingTab item={item} />}
      </Section>
    </PageArea>
  );
};

export default ItemDetail;
